'use client';
import { useRef, useEffect, useState } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { TabProps } from '.';

interface TabHeadersProps {
  tabs: TabProps[];
  currentIndex: number;
  tabLabel?: string;
  className?: string;
}

const TabHeaders = ({ tabs, currentIndex, tabLabel = '', className = '' }: TabHeadersProps) => {
  const tabRefs = useRef<(HTMLButtonElement | HTMLAnchorElement | null)[]>([]);
  const [indicatorLeft, setIndicatorLeft] = useState(0);
  const [indicatorWidth, setIndicatorWidth] = useState(0);

  const updateIndicator = () => {
    if (tabRefs.current[currentIndex]) {
      const tab = tabRefs.current[currentIndex];
      setIndicatorLeft(tab.offsetLeft);
      setIndicatorWidth(tab.offsetWidth);
    }
  };

  useEffect(() => {
    updateIndicator();
    if (tabRefs.current[currentIndex]) {
      tabRefs.current[currentIndex]?.focus();
    }
  }, [currentIndex, tabs]);

  useEffect(() => {
    const handleResize = () => {
      updateIndicator();
    };
  
    const container = tabRefs.current[currentIndex]?.parentElement;

    window.addEventListener('resize', handleResize);
  
    let resizeObserver: ResizeObserver | null = null;
    if (container) {
      resizeObserver = new ResizeObserver(() => {
        updateIndicator();
      });
      resizeObserver.observe(container);
    }
  
    updateIndicator();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver && container) {
        resizeObserver.disconnect();
      }
    };
  }, [currentIndex, tabs]);  
  
  return (
    <div
      className={classNames(
        'tw-relative tw-overflow-x-auto tw-flex',
        className
      )}
      role="tablist"
      aria-label={tabLabel}
    >
      <div
        className="tw-absolute tw-bottom-0 tw-h-1 tw-bg-action-400 tw-transition-all tw-duration-300"
        style={{ left: `${indicatorLeft}px`, width: `${indicatorWidth}px` }}
      />
      {tabs.map((tab, index) => {
        const isActive = currentIndex === index;
        const commonProps = {
          id: `tab-${index}`,
          key: tab.label,
          role: 'tab',
          tabIndex: isActive ? 0 : -1,
          'aria-selected': isActive,
          'aria-controls': `tabpanel-${index}`,
          ref: (el: HTMLButtonElement | HTMLAnchorElement | null) => {
            tabRefs.current[index] = el;
          },
          className: classNames(
            'tw-mx-4 tw-py-2 tw-whitespace-nowrap tra-text-web-heading-5 tw-cursor-pointer',
            'focus-visible:tw-outline-action-300 focus-visible:tw-outline-2',
            isActive ? 'tw-text-text-500' : 'tw-text-text-300'
          ),
        };

        return tab.href ? (
          <Anchor
            externalLink={tab.href}
            isExternal={true}
            aria-label={`${tabLabel} ${tab.label}`}
            dataTestId={`tab-anchor-${index}`}
            onKeyDown={tab.onKeyDown}
            {...commonProps}
          >
            {tab.label}
          </Anchor>
        ) : (
          <button
            onClick={() => tab.onClick?.(index)}
            onKeyDown={tab.onKeyDown}
            onFocus={tab.onFocus}
            {...commonProps}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default TabHeaders;